import { gettext } from '@eventbrite/i18n';
import { SelectField } from '@eventbrite/marmalade';
import classNames from 'classnames';
import filter from 'lodash/filter';
import sortBy from 'lodash/sortBy';
import React, { FunctionComponent } from 'react';
import type { Locale } from '../../api/models/locale';
import styles from './LanguageSelector.module.scss';

const getLanguages = (locales: Array<Locale>) => {
    const unsupportedLocales = [
        'en_IE',
        'en_NZ',
        'en_HK',
        'en_SG',
        'de_AT',
        'fr_BE',
        'nl_BE',
        'es_PE',
        'es_CL',
        'es_CO',
    ];

    return sortBy(
        // Don't display locales that aren't supported on Event Type/Language
        filter(locales, ({ locale }) => !unsupportedLocales.includes(locale)),
        'localized_language_name',
    );
};

export interface LanguageSelectorProps {
    defaultLocale: string;
    isAppRebrandActive?: boolean;
    shouldShowLanguageLabel?: boolean;
    locales: Array<Locale>;
    onChange?: (newValue: string, index: number) => void;
}

export const LanguageSelector: FunctionComponent<LanguageSelectorProps> = ({
    defaultLocale,
    locales,
    onChange,
    isAppRebrandActive = false,
    shouldShowLanguageLabel = true,
}) => {
    const options = getLanguages(locales).map((language) => ({
        label: language.localized_language_name,
        value: language.locale,
    }));

    const containerClasses = classNames(styles.languageLabelAndSelector, {
        [styles.rebranding]: isAppRebrandActive,
    });

    const onChangeHandler = (selectedLanguage: string) => {
        const index = options.findIndex(
            (option) => option.value === selectedLanguage,
        );
        if (index !== -1) {
            onChange?.(selectedLanguage, index);
        }
    };

    const showLanguageLabel = shouldShowLanguageLabel && !isAppRebrandActive;
    const label = showLanguageLabel ? gettext('Language:') : '';

    return (
        <div className={containerClasses}>
            <div className={styles.selectField}>
                <SelectField
                    label={label}
                    data-automation="language-dropdown"
                    id="language-select-field"
                    aria-label={gettext('Select your language')}
                    name="language-select-field"
                    onChange={onChangeHandler}
                    options={options}
                    value={defaultLocale}
                />
            </div>
        </div>
    );
};
